import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { 
  cityConfigModel, 
  createCityConfig, 
  validateCityConfig, 
  formatCityConfigForDisplay,
  predefinedThaiCities,
  getPredefinedCity
} from '../data/cityConfigModel';

// CRUD Operations for City Configurations

// Create a new city configuration
export const createCityConfigDocument = async (cityData, userId) => {
  try {
    console.log('🏗️ Creating city configuration document');
    console.log('👤 User ID:', userId);
    console.log('🏙️ City data:', cityData);
    
    const cityConfig = createCityConfig({
      ...cityData,
      createdBy: userId,
      updatedBy: userId
    });
    
    console.log('📋 Created city config object:', cityConfig);
    
    const validation = validateCityConfig(cityConfig);
    console.log('✅ Validation result:', validation);
    
    if (!validation.isValid) {
      console.log('❌ Validation failed:', validation.errors);
      throw new Error(`Validation failed: ${Object.values(validation.errors).join(', ')}`);
    }
    
    console.log('💾 Saving to Firestore...');
    const docRef = await addDoc(collection(db, cityConfigModel.collection), cityConfig);
    
    console.log('✅ City configuration created successfully with ID:', docRef.id);
    return {
      success: true,
      id: docRef.id,
      data: cityConfig
    };
    
  } catch (error) {
    console.error('❌ Error creating city configuration:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get all city configurations
export const getAllCityConfigs = async () => {
  try {
    console.log('📖 Fetching all city configurations...');
    
    const q = query(
      collection(db, cityConfigModel.collection),
      orderBy('cityName', 'asc')
    );
    
    const snapshot = await getDocs(q);
    const cityConfigs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('✅ Fetched city configurations:', cityConfigs.length);
    return {
      success: true,
      data: cityConfigs
    };
    
  } catch (error) {
    console.error('❌ Error fetching city configurations:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Get city configuration by ID
export const getCityConfigById = async (cityId) => {
  try {
    console.log('📖 Fetching city configuration by ID:', cityId);
    
    const docRef = doc(db, cityConfigModel.collection, cityId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const cityConfig = {
        id: docSnap.id,
        ...docSnap.data()
      };
      
      console.log('✅ City configuration found:', cityConfig.cityName);
      return {
        success: true,
        data: cityConfig
      };
    } else {
      console.log('❌ City configuration not found');
      return {
        success: false,
        error: 'City configuration not found'
      };
    }
    
  } catch (error) {
    console.error('❌ Error fetching city configuration:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get city configuration by city name
export const getCityConfigByName = async (cityName) => {
  try {
    console.log('📖 Fetching city configuration by name:', cityName);
    
    const q = query(
      collection(db, cityConfigModel.collection),
      where('cityName', '==', cityName),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      const cityConfig = {
        id: doc.id,
        ...doc.data()
      };
      
      console.log('✅ City configuration found:', cityConfig.cityName);
      return {
        success: true,
        data: cityConfig
      };
    } else {
      console.log('❌ City configuration not found');
      return {
        success: false,
        error: 'City configuration not found'
      };
    }
    
  } catch (error) {
    console.error('❌ Error fetching city configuration:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get active city configurations
export const getActiveCityConfigs = async () => {
  try {
    console.log('📖 Fetching active city configurations...');
    
    const q = query(
      collection(db, cityConfigModel.collection),
      where('status', '==', 'active'),
      orderBy('cityName', 'asc')
    );
    
    const snapshot = await getDocs(q);
    const cityConfigs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('✅ Fetched active city configurations:', cityConfigs.length);
    return {
      success: true,
      data: cityConfigs
    };
    
  } catch (error) {
    console.error('❌ Error fetching active city configurations:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Update city configuration
export const updateCityConfigDocument = async (cityId, updateData, userId) => {
  try {
    console.log('🔄 Updating city configuration:', cityId);
    console.log('👤 User ID:', userId);
    console.log('📝 Update data:', updateData);
    
    const updatePayload = {
      ...updateData,
      updatedAt: new Date().toISOString(),
      updatedBy: userId
    };
    
    // Validate the updated data
    const currentDoc = await getCityConfigById(cityId);
    if (!currentDoc.success) {
      throw new Error('City configuration not found');
    }
    
    const updatedConfig = {
      ...currentDoc.data,
      ...updatePayload
    };
    
    const validation = validateCityConfig(updatedConfig);
    if (!validation.isValid) {
      console.log('❌ Validation failed:', validation.errors);
      throw new Error(`Validation failed: ${Object.values(validation.errors).join(', ')}`);
    }
    
    console.log('💾 Updating Firestore document...');
    const docRef = doc(db, cityConfigModel.collection, cityId);
    await updateDoc(docRef, updatePayload);
    
    console.log('✅ City configuration updated successfully');
    return {
      success: true,
      data: updatedConfig
    };
    
  } catch (error) {
    console.error('❌ Error updating city configuration:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Delete city configuration
export const deleteCityConfigDocument = async (cityId) => {
  try {
    console.log('🗑️ Deleting city configuration:', cityId);
    
    const docRef = doc(db, cityConfigModel.collection, cityId);
    await deleteDoc(docRef);
    
    console.log('✅ City configuration deleted successfully');
    return {
      success: true
    };
    
  } catch (error) {
    console.error('❌ Error deleting city configuration:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Initialize predefined cities
export const initializePredefinedCities = async (userId) => {
  try {
    console.log('🚀 Initializing predefined cities...');
    
    const existingCities = await getAllCityConfigs();
    const existingCityNames = existingCities.success ? 
      existingCities.data.map(city => city.cityName) : [];
    
    const citiesToCreate = predefinedThaiCities.filter(city => 
      !existingCityNames.includes(city.cityName)
    );
    
    console.log(`📊 Found ${citiesToCreate.length} new cities to create`);
    
    const results = [];
    for (const cityData of citiesToCreate) {
      const result = await createCityConfigDocument(cityData, userId);
      results.push(result);
    }
    
    const successCount = results.filter(r => r.success).length;
    console.log(`✅ Successfully created ${successCount} city configurations`);
    
    return {
      success: true,
      created: successCount,
      total: citiesToCreate.length,
      results
    };
    
  } catch (error) {
    console.error('❌ Error initializing predefined cities:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Update city statistics
export const updateCityStats = async (cityId, statsUpdate) => {
  try {
    console.log('📊 Updating city statistics:', cityId);
    console.log('📈 Stats update:', statsUpdate);
    
    const docRef = doc(db, cityConfigModel.collection, cityId);
    const currentDoc = await getDoc(docRef);
    
    if (!currentDoc.exists()) {
      throw new Error('City configuration not found');
    }
    
    const currentData = currentDoc.data();
    const updatedStats = {
      ...currentData.stats,
      ...statsUpdate,
      lastUpdated: new Date().toISOString()
    };
    
    await updateDoc(docRef, {
      stats: updatedStats,
      updatedAt: new Date().toISOString()
    });
    
    console.log('✅ City statistics updated successfully');
    return {
      success: true,
      data: updatedStats
    };
    
  } catch (error) {
    console.error('❌ Error updating city statistics:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Search cities by name
export const searchCities = async (searchTerm) => {
  try {
    console.log('🔍 Searching cities with term:', searchTerm);
    
    const q = query(
      collection(db, cityConfigModel.collection),
      where('status', '==', 'active'),
      orderBy('cityName', 'asc')
    );
    
    const snapshot = await getDocs(q);
    const allCities = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter cities by search term (case insensitive)
    const filteredCities = allCities.filter(city => 
      city.cityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      city.cityNameEn.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    console.log(`✅ Found ${filteredCities.length} cities matching "${searchTerm}"`);
    return {
      success: true,
      data: filteredCities
    };
    
  } catch (error) {
    console.error('❌ Error searching cities:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// Get cities with Agoda integration enabled
export const getCitiesWithAgodaIntegration = async () => {
  try {
    console.log('🔗 Fetching cities with Agoda integration...');
    
    const q = query(
      collection(db, cityConfigModel.collection),
      where('status', '==', 'active'),
      where('agodaSettings.isActive', '==', true),
      orderBy('cityName', 'asc')
    );
    
    const snapshot = await getDocs(q);
    const cities = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`✅ Found ${cities.length} cities with Agoda integration`);
    return {
      success: true,
      data: cities
    };
    
  } catch (error) {
    console.error('❌ Error fetching cities with Agoda integration:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

